import { useMemo, useState } from "react";
import FixaButton from "@coworker/apprestructured/src/shared/wrappers/FixaButton/FixaButton";
import { FixaModal } from "@coworker/apprestructured/src/shared/wrappers/FixaModal/FixaModal";
import { FixaSheets } from "@coworker/apprestructured/src/shared/wrappers/FixaModal/FixaSheets";
import { FixaModalBody } from "@coworker/apprestructured/src/shared/wrappers/FixaModal/FixaModalBody";
import { useTranslation } from "@coworker/locales";
import {
  useStoreId,
  useUID,
} from "@coworker/app/src/core/auth/useLoggedInUser";
import {
  useSetNotificationAsUnread,
  useSetAllNotificationsAsUnread,
} from "../hooks/useNotifications";
import { NotificationItem } from "./NotificationItem";
import styles from "./notification.module.css";
import { FixaModalHeader } from "../../shared/wrappers/FixaModal/FixaModalHeader";
import { NotificationsInboxIcon } from "./NotificationsInboxIcon";
import trackerHelper from "@coworker/app/src/helpers/tracker";
import { useNotificationMessages } from "../hooks/useNotificationMessages";

export const NotificationsInbox = () => {
  const { t } = useTranslation();
  const storeId = useStoreId();
  const uid = useUID();
  const notifications = useNotificationMessages(storeId, uid);
  const { mutateAsync: mutateNotificationAsUnread } =
    useSetNotificationAsUnread(storeId, uid);
  const [modalVisible, setModalVisible] = useState(false);

  const unreadIdArray: string[] = useMemo(
    () =>
      notifications
        ? notifications.reduce((acc, curr) => {
            if (!curr.read) {
              acc.push(curr.id);
            }
            return acc;
          }, [] as string[])
        : [],
    [notifications]
  );
  const { mutateAsync: mutateAllNotificationsAsUnread } =
    useSetAllNotificationsAsUnread(storeId, uid);

  const onMarkAllRead = () => {
    if (unreadIdArray.length) {
      mutateAllNotificationsAsUnread(unreadIdArray);
    }
  };

  const inboxStyles = [styles["notification-inbox"] ?? ""];
  if (unreadIdArray.length) {
    inboxStyles.push(styles["notification-inbox-with-unread"] ?? "");
  }

  const unreadCount = unreadIdArray.length;
  const hasNotificationsData = Boolean(storeId && uid);
  const inboxAriaLabel =
    unreadCount > 0
      ? `${t("notificationsString")} (${unreadCount})`
      : t("notificationsString");

  return (
    <div className={inboxStyles.join(" ")}>
      <NotificationsInboxIcon
        unreadCount={unreadCount}
        hasNotificationsData={hasNotificationsData}
        ariaLabel={inboxAriaLabel}
        onClick={() => {
          setModalVisible(!modalVisible);
          trackerHelper.notifications.trackMenuOpen();
        }}
      />
      <FixaModal
        visible={modalVisible}
        handleCloseBtn={() => {
          setModalVisible(false);
        }}
      >
        <FixaSheets
          labelledById="notifications-inbox-header"
          header={
            <FixaModalHeader
              id="notifications-inbox-header"
              ariaCloseTxt={t("closeActionString")}
            />
          }
          footer={null}
          fullSize={true}
        >
          <FixaModalBody className={styles["notification-inbox-body"] ?? ""}>
            <div className={styles["notification-inbox-header"] ?? ""}>
              <div>
                <span>{t("notificationsString")}</span>
              </div>
              <FixaButton
                aria-label={t("markAllReadWithCountString", {
                  count: unreadIdArray.length,
                })}
                data-testid={"mark all read button"}
                text={t("markAllReadWithCountString", {
                  count: unreadIdArray.length,
                })}
                type="tertiary"
                inverseTheme
                onClick={() => {
                  onMarkAllRead();
                  trackerHelper.notifications.trackMarkAllRead();
                }}
              />
            </div>
            {hasNotificationsData &&
              notifications?.map((notification) => (
                <NotificationItem
                  key={notification.id}
                  notification={notification}
                  onClick={(notificationId) => {
                    setModalVisible(false);
                    mutateNotificationAsUnread(notificationId);
                    trackerHelper.notifications.trackItemClick();
                  }}
                />
              ))}
          </FixaModalBody>
        </FixaSheets>
      </FixaModal>
    </div>
  );
};
